import React, { useState } from 'react';
import {
  Eye,
  Lightbulb,
  MessageCircle,
  X,
  Info,
  Sparkles,
  Clock,
  Users,
  Construction
} from 'lucide-react';
import UserFeedbackWidget from '../feedback/UserFeedbackWidget';

interface ComingSoonBannerProps {
  featureName: string;
  description: string;
  currentPage: string;
  variant?: 'default' | 'compact';
  expectedRelease?: string;
  demoAvailable?: boolean;
  onTryDemo?: () => void;
}

export const ComingSoonBanner: React.FC<ComingSoonBannerProps> = ({
  featureName,
  description,
  currentPage,
  variant = 'default',
  expectedRelease = "very soon",
  demoAvailable = true,
  onTryDemo
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showFeedbackWidget, setShowFeedbackWidget] = useState(false);

  if (isDismissed) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return 'p-3 text-sm';
      case 'inline':
        return 'p-2 text-xs inline-flex items-center space-x-2 rounded-lg';
      default:
        return 'p-6';
    }
  };

  const getIconSize = () => {
    switch (variant) {
      case 'compact':
        return 'h-4 w-4';
      case 'inline':
        return 'h-3 w-3';
      default:
        return 'h-5 w-5';
    }
  };

  if (variant === 'inline') {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-blue-700 p-2 text-xs inline-flex items-center space-x-2 rounded-lg">
        <Sparkles className="h-3 w-3" />
        <span className="font-medium">Coming Soon</span>
        {demoAvailable && (
          <button 
            onClick={onTryDemo}
            className="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
          >
            <Eye className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${
      variant === 'compact' 
        ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 p-4' 
        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 p-6'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Construction className={`h-5 w-5 ${variant === 'compact' ? 'text-amber-600' : 'text-blue-600'}`} />
            <h3 className={`font-semibold ${
              variant === 'compact' 
                ? 'text-amber-900 text-base' 
                : 'text-blue-900 text-lg'
            }`}>
              {featureName} {demoAvailable && '(Demo Available)'}
            </h3>
          </div>
          <p className={`${
            variant === 'compact' 
              ? 'text-amber-800 text-sm' 
              : 'text-blue-800 text-base'
          } mb-3`}>
            {description}
          </p>
          
          {demoAvailable && (
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">✅ Working Now in Demo</p>
                  <p className="text-xs text-slate-600">Add your own data, everything saves locally</p>
                </div>
                {onTryDemo && (
                  <button 
                    onClick={onTryDemo}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    Try It
                  </button>
                )}
              </div>
            </div>
          )}
          
          <p className={`text-xs ${
            variant === 'compact' ? 'text-amber-600' : 'text-blue-600'
          } mt-2`}>
            Full production release expected {expectedRelease} • Page: {currentPage}
          </p>
        </div>
        <button 
          onClick={() => setIsDismissed(true)}
          className={`${
            variant === 'compact' ? 'text-amber-600 hover:text-amber-800' : 'text-blue-600 hover:text-blue-800'
          } ml-4 text-sm font-medium`}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default ComingSoonBanner; 
